// Project launch progress (decision D28, docs/delivery/microsystem-plan.md §1): a compact card on the Kanban of a project
// created from a brief (or connected from GitHub) that walks the four steps to a running app — the repository clone
// (GET …/repository/provision), the latest planning run, "Start agents" (POST …/execution start, enabled once the clone
// is ready and planning finished) and "Publish to Coolify" (POST …/publish, then GET …/publish every 3 s until a final
// state). Renders nothing for a project without a provision. Every refusal shows the API's own message (ActionError:
// readiness/configuration impediments included); progress is announced politely to screen readers.
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { Board, ProjectSummary, RepositoryProvision } from "@maestros/contracts";
import { useI18n } from "../../i18n/index.tsx";
import type { MessageKey } from "../../i18n/en.ts";
import { changeExecution, getProjectDetail } from "../../api/endpoints.ts";
import { getProvision } from "../../api/endpoints-onboarding.ts";
import { listPlanningRuns } from "../../api/endpoints-wave5.ts";
import type { PlanningRun } from "../../api/types-wave5.ts";
import { FINAL_PUBLISH_STATES, getProjectPublish, publishProject, type ProjectPublish, type PublishState } from "../../api/endpoints-microsystem.ts";
import type { AsyncState } from "../../lib/useAsync.ts";
import { executionFacts } from "../../lib/taskExecution.ts";
import { useOrgRole } from "../config/shared.tsx";
import { useProjectRole } from "../wave3/common.tsx";
import { Button } from "../../components/Button.tsx";
import { ActionError } from "../../components/ActionError.tsx";
import { Pill } from "../../components/Status.tsx";
import { useToast } from "../../components/Toast.tsx";
import { ExternalLinkIcon, PlayIcon, UploadIcon } from "../../components/icons.tsx";
import type { IconKind, Tone } from "../../lib/status.ts";

export const PROVISION_POLL_MS = 3000;
export const PLANNING_POLL_MS = 5000;
export const PUBLISH_POLL_MS = 3000;

const PUBLISH_STEPS: readonly PublishState[] = ["pushing", "creating_app", "deploying", "succeeded"];
/** Position of each publish state along PUBLISH_STEPS (1-based); succeeded is past all of them. */
const PUBLISH_REACHED: Record<Exclude<PublishState, "failed" | "creating_repository">, number> = { queued: 0, pushing: 1, creating_app: 2, deploying: 3, succeeded: 5 };
/** D33: while the publish creates the project's repository, that step leads the list (a mapped project never shows it). */
const CREATING_REPOSITORY_STEPS: readonly PublishState[] = ["creating_repository", ...PUBLISH_STEPS];

type StepTone = { tone: Tone; icon: IconKind };
const DONE: StepTone = { tone: "success", icon: "check" };
const BUSY: StepTone = { tone: "info", icon: "spinner" };
const WAIT: StepTone = { tone: "neutral", icon: "circle" };
const FAIL: StepTone = { tone: "danger", icon: "x" };
// A domain state that merely is active (agents working) — the spinner is reserved for operations in flight.
const ACTIVE: StepTone = { tone: "info", icon: "activity" };

/** Polls `load` every `ms` while `keepPolling(latest)` holds; `undefined` until the first answer. */
export function usePolled<T>(load: (signal: AbortSignal) => Promise<T>, ms: number, keepPolling: (value: T) => boolean, deps: unknown[]): { value: T | undefined; error: unknown; reload: () => void } {
  const [value, setValue] = useState<T | undefined>(undefined);
  const [error, setError] = useState<unknown>(null);
  const [tick, setTick] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;
  const keepRef = useRef(keepPolling);
  keepRef.current = keepPolling;
  useEffect(() => {
    const controller = new AbortController();
    let timer: number | undefined;
    const run = async () => {
      let again = true;
      try {
        const next = await loadRef.current(controller.signal);
        if (controller.signal.aborted) return;
        setValue(next);
        setError(null);
        again = keepRef.current(next);
      } catch (e) {
        if (controller.signal.aborted) return;
        setError(e);
      }
      if (again && !controller.signal.aborted) timer = window.setTimeout(() => void run(), ms);
    };
    void run();
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick, ms]);
  return { value, error, reload: () => setTick((n) => n + 1) };
}

/** Every task of the board is finished (done or cancelled) and at least one is done. */
export function allTasksDone(board: Board | undefined): boolean {
  if (!board) return false;
  const tasks = [...board.columns.flatMap((c) => c.tasks), ...board.side_lane.flatMap((s) => s.tasks)];
  return tasks.some((t) => t.status === "done") && tasks.every((t) => t.status === "done" || t.status === "cancelled");
}

function githubUrl(repository: string | null): string | null {
  return repository && /^[\w.-]+\/[\w.-]+$/.test(repository) ? `https://github.com/${repository}` : null;
}

/** Only an http(s) app URL is ever rendered as a link. */
export function safeHttpUrl(url: string | null): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:" ? parsed.toString() : null;
  } catch {
    return null;
  }
}

function provisionTone(p: RepositoryProvision): StepTone {
  return p.state === "done" ? DONE : p.state === "failed" ? FAIL : BUSY;
}

function planningTone(run: PlanningRun | null): StepTone {
  if (!run) return WAIT;
  return run.state === "completed" ? DONE : run.state === "failed" ? FAIL : BUSY;
}

function Step({ index, title, status, testId, state, children }: { index: number; title: string; status: { label: string; tone: StepTone }; testId: string; state: string; children?: ReactNode }) {
  return (
    <li className="launch-step" data-testid={testId} data-state={state}>
      <div className="launch-step-head">
        <span className="launch-step-index" aria-hidden="true">
          {index}
        </span>
        <h3 className="launch-step-title">{title}</h3>
        <Pill tone={status.tone.tone} icon={status.tone.icon}>
          {status.label}
        </Pill>
      </div>
      {children ? <div className="launch-step-body">{children}</div> : null}
    </li>
  );
}

export function LaunchCard({ project, board, onProjectChanged }: { project: ProjectSummary; board: AsyncState<Board>; onProjectChanged: () => void }) {
  const { t, formatRelative } = useI18n();
  const { toast } = useToast();
  const isOrgAdmin = useOrgRole().atLeast("admin");
  const canStart = useProjectRole(project.id).atLeast("maintainer");
  const [startError, setStartError] = useState<unknown>(null);
  const [starting, setStarting] = useState(false);
  const [publishError, setPublishError] = useState<unknown>(null);
  const [publishing, setPublishing] = useState(false);

  const provision = usePolled((s) => getProvision(project.id, s).then((r) => r.provision), PROVISION_POLL_MS, (p) => p !== null && p.state !== "done" && p.state !== "failed", [project.id]);
  const planning = usePolled(
    (s) => listPlanningRuns(project.id, s).then((r) => r.items[0] ?? null),
    PLANNING_POLL_MS,
    (r) => r === null || r.state === "requested" || r.state === "running",
    [project.id],
  );
  const publish = usePolled(
    (s) => getProjectPublish(project.id, s).then((r) => r.publish),
    PUBLISH_POLL_MS,
    (p) => p !== null && !FINAL_PUBLISH_STATES.includes(p.state),
    [project.id],
  );

  const p = provision.value;
  const run = planning.value ?? null;
  const pub: ProjectPublish | null = publish.value ?? null;
  const done = useMemo(() => allTasksDone(board.data), [board.data]);

  const provisionLabel = (prov: RepositoryProvision): string => {
    if (prov.state === "done") return t("launch.ready");
    if (prov.state === "failed") return t("launch.failed");
    if (prov.state === "queued") return t("launch.queued");
    return t(`githubOnboarding.step.${prov.state}` as MessageKey);
  };

  // Announce only real transitions (not every poll) through one polite live region.
  const status = p ? `${p.state}|${run?.state ?? "none"}|${project.state}|${pub?.state ?? "none"}` : "";
  const [announcement, setAnnouncement] = useState("");
  const previous = useRef<string | null>(null);
  useEffect(() => {
    if (!p) return;
    if (previous.current !== null && previous.current !== status) {
      const parts = [t("launch.repository"), provisionLabel(p), t("launch.planning"), run ? t(`planning.state.${run.state}` as MessageKey) : t("launch.planningNone")];
      if (pub) parts.push(t("launch.publish"), t(`launch.publishState.${pub.state}` as MessageKey));
      setAnnouncement(parts.join(" — "));
    }
    previous.current = status;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Nothing to launch: a project never connected from GitHub nor created from a brief.
  if (p === undefined || p === null) return null;

  const provisionReady = p.state === "done";
  const planningDone = run?.state === "completed";
  const execution = executionFacts(board.error ? undefined : board.data);
  const startable = project.state === "draft" || project.state === "stopped";
  const startEnabled = provisionReady && planningDone && startable && canStart;
  const publishBusy = pub !== null && !FINAL_PUBLISH_STATES.includes(pub.state);
  const repoUrl = githubUrl(pub?.repository ?? p.repository);
  const appUrl = safeHttpUrl(pub?.app_url ?? null);

  const start = async () => {
    setStarting(true);
    setStartError(null);
    try {
      const fresh = await getProjectDetail(project.id);
      await changeExecution(project.id, "start", fresh.version);
      toast(t("launch.executionEnabled"), "success");
      onProjectChanged();
    } catch (e) {
      setStartError(e);
    } finally {
      setStarting(false);
    }
  };

  const doPublish = async () => {
    setPublishing(true);
    setPublishError(null);
    try {
      await publishProject(project.id);
      toast(t("launch.publishRequested"), "success");
      publish.reload();
    } catch (e) {
      setPublishError(e);
    } finally {
      setPublishing(false);
    }
  };

  const agentsStatus = execution.running > 0
    ? { label: t("launch.agentsRunning"), tone: ACTIVE }
    : execution.waiting > 0
      ? { label: t("launch.agentsWaiting"), tone: WAIT }
      : execution.unknown
        ? { label: t("kanban.card.executionUnknown"), tone: WAIT }
        : done
          ? { label: t("launch.agentsDone"), tone: DONE }
          : { label: t("kanban.card.noActiveAttempt"), tone: WAIT };
  const agentsState = execution.running > 0 ? "running" : execution.waiting > 0 ? "waiting_resource" : execution.unknown ? "unknown" : done ? "done" : "idle";
  const publishStatus = pub ? { label: t(`launch.publishState.${pub.state}` as MessageKey), tone: pub.state === "succeeded" ? DONE : pub.state === "failed" ? FAIL : BUSY } : { label: t("launch.publishNone"), tone: WAIT };

  return (
    <section className="card launch-card" aria-labelledby={`launch-${project.id}`} data-testid="launch-card">
      <div className="card-header">
        <h2 id={`launch-${project.id}`} className="card-title" style={{ flex: 1 }}>
          {t("launch.title")}
        </h2>
        {repoUrl ? (
          <a className="link-subtle row" href={repoUrl} target="_blank" rel="noreferrer noopener" data-testid="launch-repository-link">
            <span className="mono">{pub?.repository ?? p.repository}</span>
            <ExternalLinkIcon size={14} />
            <span className="sr-only">{t("launch.opensInNewTab")}</span>
          </a>
        ) : null}
      </div>
      <div className="card-body">
        <p className="sr-only" aria-live="polite" data-testid="launch-announcer">
          {announcement}
        </p>
        <ol className="launch-steps">
          <Step index={1} title={t("launch.repository")} status={{ label: provisionLabel(p), tone: provisionTone(p) }} testId="launch-step-repository" state={p.state}>
            {p.state === "failed" ? <p className="launch-error">{t(`githubOnboarding.error.${p.error_code ?? "clone_failed"}` as MessageKey)}</p> : null}
            {provision.error ? <ActionError error={provision.error} /> : null}
          </Step>

          <Step
            index={2}
            title={t("launch.planning")}
            status={{ label: run ? t(`planning.state.${run.state}` as MessageKey) : t("launch.planningNone"), tone: planningTone(run) }}
            testId="launch-step-planning"
            state={run?.state ?? "none"}
          >
            {run?.state === "completed" ? <p className="subtle">{t("planning.tasksCreated", { n: run.tasks_created })}</p> : null}
            {run?.state === "failed" && run.error ? <p className="launch-error user-content">{run.error}</p> : null}
            {planning.error ? <ActionError error={planning.error} /> : null}
          </Step>

          <Step index={3} title={t("launch.agents")} status={agentsStatus} testId="launch-step-agents" state={agentsState}>
            {startable ? (
              <div className="stack" style={{ gap: 6 }}>
                <div>
                  <Button size="sm" variant="primary" icon={<PlayIcon size={14} />} loading={starting} disabled={!startEnabled} onClick={() => void start()} data-testid="launch-start" aria-describedby={`launch-start-hint-${project.id}`}>
                    {t("launch.startAgents")}
                  </Button>
                </div>
                <p id={`launch-start-hint-${project.id}`} className="field-hint">
                  {!canStart ? t("launch.startNeedsRole") : !provisionReady ? t("launch.startWaitsRepository") : !planningDone ? t("launch.startWaitsPlanning") : t("launch.startHint")}
                </p>
              </div>
            ) : null}
            <ActionError error={startError} onDismiss={() => setStartError(null)} />
          </Step>

          <Step index={4} title={t("launch.publish")} status={publishStatus} testId="launch-step-publish" state={pub?.state ?? "none"}>
            {pub && pub.state !== "queued" ? (
              <ol className="launch-substeps" aria-label={t("launch.publishProgress")}>
                {(pub.state === "creating_repository" ? CREATING_REPOSITORY_STEPS : PUBLISH_STEPS).map((s, i) => {
                  const reached = pub.state === "failed" ? -1 : pub.state === "creating_repository" ? 1 : PUBLISH_REACHED[pub.state];
                  const st = reached > i + 1 ? "done" : reached === i + 1 ? "current" : "pending";
                  return (
                    <li key={s} data-testid="launch-publish-step" data-state={st}>
                      <Pill tone={st === "done" ? "success" : st === "current" ? "info" : "neutral"} icon={st === "done" ? "check" : st === "current" ? "spinner" : "circle"}>
                        {t(`launch.publishState.${s}` as MessageKey)}
                        <span className="sr-only"> — {t(`githubOnboarding.stepState.${st}` as MessageKey)}</span>
                      </Pill>
                    </li>
                  );
                })}
              </ol>
            ) : null}
            {appUrl ? (
              <p>
                <a className="btn btn--sm btn--outline-accent" href={appUrl} target="_blank" rel="noreferrer noopener" data-testid="launch-app-url">
                  <ExternalLinkIcon size={14} />
                  {t("launch.openApp")}
                  <span className="sr-only"> ({appUrl})</span>
                </a>
              </p>
            ) : null}
            {pub?.state === "failed" ? (
              <p className="launch-error" data-testid="launch-publish-error">
                <span>{t("launch.publishFailed")}</span>
                {pub.error ? <span className="user-content"> {pub.error}</span> : null}
                {pub.error_code ? <span className="mono subtle"> ({pub.error_code})</span> : null}
              </p>
            ) : null}
            {pub?.finished_at ? <p className="subtle">{t("launch.publishFinished", { when: formatRelative(pub.finished_at) })}</p> : null}
            {isOrgAdmin ? (
              <div className="stack" style={{ gap: 6 }}>
                <div>
                  <Button size="sm" variant={done ? "primary" : "secondary"} icon={<UploadIcon size={14} />} loading={publishing} disabled={publishBusy} onClick={() => void doPublish()} data-testid="launch-publish">
                    {pub?.state === "succeeded" ? t("launch.republish") : t("launch.publishButton")}
                  </Button>
                </div>
                {!done ? <p className="field-hint">{t("launch.publishBeforeDone")}</p> : null}
              </div>
            ) : !pub ? (
              <p className="field-hint">{t("launch.publishNeedsAdmin")}</p>
            ) : null}
            {publish.error ? <ActionError error={publish.error} /> : null}
            <ActionError error={publishError} onDismiss={() => setPublishError(null)} />
          </Step>
        </ol>
      </div>
    </section>
  );
}
